import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MESSAGES = [
  "Suiting up your pup...",
  "Negotiating the rookie contract...",
  "Getting the stadium ready...",
  "Applying the fur texture to the jersey...",
  "Your legend is almost ready...",
  "Adjusting the helmet fit...",
  "Warming up on the field...",
  "Signing autographs for the fans...",
];

export default function GeneratingPage() {
  useSEO({ title: "Generating Your Card", description: "Your AI sports trading card is being generated. Hang tight!", path: "/generating" });
  const { cardId } = useParams();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [messageIdx, setMessageIdx] = useState(0);
  const [dogName, setDogName] = useState("");
  const [isLong, setIsLong] = useState(false);
  const [failed, setFailed] = useState(false);
  const [failMessage, setFailMessage] = useState("");
  const startTime = useRef(Date.now());

  // Fetch card info
  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${API}/cards/${cardId}`);
        setDogName(resp.data.dog_name || "Your dog");
      } catch {
        // ignore
      }
    })();
  }, [cardId]);

  // Progress animation
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 95) return 95;
        return p + (95 - p) * 0.08;
      });
    }, 300);
    return () => clearInterval(interval);
  }, []);

  // Rotate messages
  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIdx((i) => (i + 1) % MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Poll status
  useEffect(() => {
    const poll = setInterval(async () => {
      try {
        const resp = await axios.get(`${API}/cards/${cardId}/status`);
        const { status } = resp.data;

        if (status === "preview_ready") {
          setProgress(100);
          clearInterval(poll);
          // GA4: card_generated event
          if (window.gtag) window.gtag('event', 'card_generated', { card_id: cardId });
          setTimeout(() => navigate(`/preview/${cardId}`), 500);
        } else if (status === "failed") {
          clearInterval(poll);
          setFailed(true);
          // Use error reason from card if available
          const reason = resp.data.fal_request_id;
          if (reason && !reason.startsWith("req_")) {
            setFailMessage(reason);
          }
        }

        // Check if taking too long
        if (Date.now() - startTime.current > 20000) {
          setIsLong(true);
        }
      } catch {
        // Retry on next poll
      }
    }, 2000);

    return () => clearInterval(poll);
  }, [cardId, navigate]);

  const handleRetry = async () => {
    setFailed(false);
    setProgress(0);
    startTime.current = Date.now();
    try {
      await axios.post(`${API}/cards/${cardId}/regenerate`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Generation failed. Please try creating a new card.";
      setFailed(true);
      setFailMessage(detail);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: "var(--color-navy)" }}
      data-testid="generating-page"
    >
      <div className="max-w-md mx-auto px-4 text-center">
        {failed ? (
          <div className="animate-fade-in">
            <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: "rgba(231,76,60,0.15)" }}>
              <span className="text-3xl">:(</span>
            </div>
            <h2 className="font-heading text-3xl uppercase tracking-tight text-white mb-3">
              Fumbled the play
            </h2>
            <p className="font-body text-white/60 mb-8">
              {failMessage || "Something went wrong during generation. Let's try that again."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handleRetry}
                className="font-heading tracking-widest uppercase px-8 py-4 rounded-[8px] transition-colors"
                style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
                data-testid="retry-btn"
              >
                Try Again
              </button>
              <a
                href="/create"
                className="font-heading tracking-widest uppercase px-8 py-4 rounded-[8px] transition-colors border-2 border-white/30 text-white hover:bg-white/10"
              >
                Start Over
              </a>
            </div>
          </div>
        ) : (
          <>
            {/* Animated paw */}
            <div className="mb-8">
              <svg
                className="w-24 h-24 mx-auto animate-paw-pulse"
                viewBox="0 0 100 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="35" cy="25" rx="10" ry="12" fill="var(--color-gold)" opacity="0.8"/>
                <circle cx="65" cy="25" rx="10" ry="12" fill="var(--color-gold)" opacity="0.8"/>
                <circle cx="20" cy="45" rx="9" ry="11" fill="var(--color-gold)" opacity="0.8"/>
                <circle cx="80" cy="45" rx="9" ry="11" fill="var(--color-gold)" opacity="0.8"/>
                <ellipse cx="50" cy="65" rx="20" ry="18" fill="var(--color-gold)"/>
              </svg>
            </div>

            {/* Dog name */}
            <h2 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-white mb-2" data-testid="generating-title">
              {dogName} is getting their close-up
            </h2>

            {/* Rotating message */}
            <p className="font-body text-white/60 mb-8 h-6 transition-all" data-testid="generating-message">
              {MESSAGES[messageIdx]}
            </p>

            {/* Progress bar */}
            <div className="w-full h-2 rounded-full mb-3" style={{ backgroundColor: "rgba(255,255,255,0.1)" }}>
              <div
                className="progress-bar-fill rounded-full"
                style={{ width: `${Math.round(progress)}%` }}
                data-testid="progress-bar"
              />
            </div>
            <p className="font-accent text-xs text-white/40">
              {Math.round(progress)}%
            </p>

            {isLong && (
              <p className="font-body text-sm text-white/50 mt-6 animate-fade-in">
                Almost there &mdash; our AI is putting the finishing touches on.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
